import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';

import { StackedContentTypeIcons } from './StackedContentTypeIcons.tsx';
import { ThirdPartyAuthenticationModal } from './ThirdPartyAuthenticationModal.tsx';
import {
	type AuthenticationStatus,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

const i18n = defineMessages({
	cardTitle: {
		id: 'space-curation.review-content-suggestions-modal.connect-apps-card.title',
		defaultMessage: 'Connect apps for more suggestions',
		description:
			'Title on the connect third-party apps card in the review content suggestions modal',
	},
	connectApps: {
		id: 'space-curation.review-content-suggestions-modal.connect-apps-card.button',
		defaultMessage: 'Connect apps',
		description:
			'Button that opens the third-party authentication modal from the review suggestions flow',
	},
	dismiss: {
		id: 'space-curation.review-content-suggestions-modal.connect-apps-card.dismiss',
		defaultMessage: 'Dismiss connect apps suggestion',
		description: 'Accessible label for dismissing the connect apps card',
	},
});

const styles = cssMap({
	card: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.large'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		flexShrink: 0,
	},
	headerRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.100'),
	},
	dismissButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: token('space.050'),
		flexShrink: 0,
		backgroundColor: 'transparent',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects token values; transparent needed for hover
			backgroundColor: 'transparent',
		},
	},
});

type ConnectAppsForMoreSuggestionsCardProps = {
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	onThirdPartyAuthRefetch: () => void | Promise<void>;
	spaceName?: string;
	iconPath?: string;
};

export const ConnectAppsForMoreSuggestionsCard = ({
	unauthenticatedServices,
	onThirdPartyAuthRefetch,
	spaceName,
	iconPath,
}: ConnectAppsForMoreSuggestionsCardProps) => {
	const intl = useIntl();
	const [isDismissed, setIsDismissed] = useState(false);
	const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

	useEffect(() => {
		if (Object.keys(unauthenticatedServices).length === 0 && isAuthModalOpen) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- close nested modal when parent reports nothing left to connect
			setIsAuthModalOpen(false);
		}
	}, [unauthenticatedServices, isAuthModalOpen]);

	const handleAuthModalClose = () => {
		setIsAuthModalOpen(false);
		void onThirdPartyAuthRefetch();
	};

	if (Object.keys(unauthenticatedServices).length === 0) {
		return null;
	}

	return (
		<>
			{!isDismissed ? (
				<Box xcss={styles.card} testId="review-content-suggestions-connect-apps-card">
					<Flex xcss={styles.headerRow}>
						<Box>
							<StackedContentTypeIcons
								variant="large"
								testId="review-content-suggestions-connect-apps-icons"
							/>
						</Box>
						<Pressable
							xcss={styles.dismissButton}
							onClick={() => setIsDismissed(true)}
							aria-label={intl.formatMessage(i18n.dismiss)}
						>
							<CrossIcon label="" />
						</Pressable>
					</Flex>
					<Text weight="medium">{intl.formatMessage(i18n.cardTitle)}</Text>
					<Box>
						<Button appearance="default" onClick={() => setIsAuthModalOpen(true)}>
							{intl.formatMessage(i18n.connectApps)}
						</Button>
					</Box>
				</Box>
			) : null}
			<ModalTransition>
				{isAuthModalOpen && (
					<ThirdPartyAuthenticationModal
						unauthenticatedServices={unauthenticatedServices}
						onClose={handleAuthModalClose}
						spaceName={spaceName}
						iconPath={iconPath}
					/>
				)}
			</ModalTransition>
		</>
	);
};
